import { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { DashboardContext } from '../../DashboardContext';
import { DashboardTableHeader } from '../DashboardTableHeader';
import { api } from '../../../functions/api';
import { ApiPath } from '../../../api/ApiPath';
import { DashboardGateQuestion } from '../../types/DashboardGateQuestion';
import './DashboardTableGateQuestions.scss';

function assetName(question: DashboardGateQuestion): string {
  // Named the way the bot names it: what it is, then its number. The title is a licence
  // plate and means nothing to the person reading this.
  return [question.subcategory || question.category, question.guid].filter(Boolean).join(' ');
}

export const DashboardTableGateQuestions: FC = observer(() => {
  const { t } = useTranslation();
  const { gateQuestions } = useContext(DashboardContext);
  const [dismissing, setDismissing] = useState<string | null>(null);

  const questions = gateQuestions.data?.questions ?? [];

  const dismiss = async (question: DashboardGateQuestion) => {
    setDismissing(question.eventId);
    try {
      await api({ url: `${ApiPath.DashboardGateQuestions}/${question.eventId}/dismiss`, method: 'PUT' });
      gateQuestions.reload();
    } finally {
      setDismissing(null);
    }
  };

  return (
    <>
      <DashboardTableHeader
        title={t('Unexplained departures')}
        description={t(
          'Seen leaving a site and not seen since. Still shown at that site until somebody says what happened — recording the movement closes it.',
        )}
      />

      {questions.length === 0 ? (
        <div className="DashboardTableGateQuestions__empty">{t('Nothing unexplained right now.')}</div>
      ) : (
        <table className="DashboardTableGateQuestions">
          <thead>
            <tr>
              <th>{t('Asset')}</th>
              <th>{t('Left from')}</th>
              <th>{t('Seen at')}</th>
              <th>{t('Asked')}</th>
              <th />
            </tr>
          </thead>

          <tbody>
            {questions.map((question) => (
              <tr key={question.eventId}>
                <td>{assetName(question)}</td>
                <td>{question.fromSiteName ?? '—'}</td>
                <td>{new Date(question.detectedAt).toLocaleString()}</td>
                <td>{question.askedCount > 0 ? question.askedCount : '—'}</td>
                <td>
                  <button
                    type="button"
                    className="dismiss"
                    onClick={() => dismiss(question)}
                    disabled={dismissing === question.eventId}
                  >
                    {t('Dismiss')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
});
